/**
 * Générateur — Distributivité simple : développer un produit, factoriser une
 * somme, réduire une expression littérale.
 * Attendu ID : 159 | Niveau : 4ème | Domaine : Calcul littéral (O23)
 *
 * Source : doc prof « TANOED EXERCISEUR … 4EME », NOMBRE ET CALCUL, cluster 3,
 * O23. Critère de réussite (image page30) :
 *   développe et réduit : 3(4x − 2) ; 3x(4 + 8x) ; 17x + 4x(5 − x) ; 6(3 − 1,5x) − 9x
 *   factorise : 12x − 30 ; 15x² + 18x ; 27x² + 3
 * Le 4e va plus loin que le 5e (gens 936/937) : termes en x², distributivité
 * simple par un facteur littéral, px(a ± bx), développer PUIS réduire.
 *
 * Plan de cartes (validé 2026-07-14) :
 *   developper — k(ax ± b) et px(a ± bx) : saisie (trous = coefficients) + qcm.
 *   reduire    — px + qx(r ± sx) et k(a − bx) − cx : développer PUIS réduire.
 *   factoriser — Ax ± B, Ax² + Bx (facteur gx), Ax² + B : saisie + qcm.
 *   egalite    — vrai/faux de développements (pièges classiques du doc).
 *
 * ── Revue 2026-09-23 (vague 153-163) ──────────────────────────────────────
 * Plafond, attendus de fin de 4e (Eduscol, p. 4) : « Il utilise la propriété
 * de distributivité simple pour développer un produit, factoriser une somme
 * ou réduire une expression littérale. » Exemples : développer et réduire
 * « 3(4x − 2) ; 3x(4 + 8x) ; 17x + 4x(5 − x) ; 6(3 − 1,5x) − 9x » ; factoriser
 * « 12x − 30 ; 15x² + 18x ; 27x² + 3 ». Le facteur extérieur NÉGATIF et la
 * soustraction d'un produit sont des exemples de 3e, hors plafond : on n'en
 * tire pas. L'ancienne « ADAPTATION entiers » est levée, les décimaux sont
 * revenus (carte « Développer puis réduire »).
 *   - Toutes les cartes : expressions, choix, lignes du Vrai/Faux et phrases à
 *     trous en KaTeX, lettre comprise (ligne entière en $…$ dans les trous,
 *     « x^2 », « - », jamais « 1x »). Rappel brouillon IMPÉRATIF sur les sept
 *     formats (RAPPEL_BROUILLON), juste avant la question : les exemples
 *     officiels demandent deux étapes, un facteur commun, des x².
 *   - « Développer un produit », saisie : le coefficient 1 s'ouvre
 *     (« 5(x − 3) », écrit « x »), la constante en tête aussi (cours :
 *     2(3 + x) = 6 + 2x) ; facteur extérieur positif de 2 à 9.
 *   - « Développer un produit », QCM : distracteurs de gen_1230 (DEPP 2de 2025
 *     q.5) : un_seul_terme (principale), termes_non_semblables, et signe_perdu
 *     quand un moins est en jeu, addition_pour_produit sinon. Avant, chaque
 *     distracteur gardait un nombre non multiplié et la clé était « le choix
 *     aux plus grands nombres » dans 100 % des tirages (0 % maintenant). Mode
 *     auto : avec « − », a > b par construction, sinon termes_non_semblables
 *     (ka − kb)x tomberait à 0 ou en négatif ; « k(x − b) » ne sort donc qu'en
 *     saisie. Résidu mesuré, à arbitrer : avec « + », la clé est seule à avoir
 *     la plus grande constante dans 43,7 % des tirages (kb dépasse b et k + b),
 *     aucune des trois familles retenues ne porte une constante plus grande.
 *   - « Développer puis réduire », saisie : p jusqu'à 20 et s de 1 à 9
 *     (17x + 4x(5 − x) devient tirable) ; UN TIRAGE SUR QUATRE à coefficient
 *     décimal, k(a − d·x) − c·x avec k pair et d dans {0,5 ; 1,5 ; 2,5}, donc
 *     k × d entier (6 × 1,5 = 9, l'exemple 6(3 − 1,5x) − 9x), marqué
 *     contenu.difficulte = 'coefficient_decimal' et décrit dans
 *     ECHELONS_DIFFICULTE (FORMATS §0.8) : mode auto, À ARBITRER (le taux et
 *     les trois valeurs de d). Décimaux écrits « 1{,}5 » dans le LaTeX.
 *   - « Développer puis réduire », QCM : familles priorite ((p + q)x(r − sx)
 *     développé, l'erreur attendue sur 17x + 4x(5 − x)), signe_perdu,
 *     carre_oublie (x × x écrit x ; repli nommé un_seul_terme quand ce
 *     coefficient tomberait à 0 ou en dessous). Avant, la clé était seule en
 *     tête à la majorité des composantes dans 100 % des tirages ; maintenant
 *     jamais seule sur les composantes de la simulation de l'audit (x², x,
 *     constante et son signe), seule dans 76 % si l'on compte aussi le signe du
 *     x² (elle le partage avec priorite). Même domaine que la saisie (p
 *     jusqu'à 20, s de 1 à 9), mode auto.
 *   - « Factoriser une somme », saisie : consigne « Factorise le plus
 *     possible » sur les trois formes (A1 de consigne : la forme Ax² + Bx
 *     disait « le facteur commun », et 15 % des tirages admettaient une autre
 *     factorisation juste, avec un facteur de 2 ou plus, comptée fausse) ; le
 *     coefficient 1 s'ouvre (gabarit g(x ± v), cours 7x + 42 et 3x − 3) ;
 *     constante en tête une fois sur trois dans la forme affine
 *     (24 − 8x = 8(3 − x)), facteur de 2 à 9 dans cette forme (2 à 7 avant)
 *     pour que l'exemple du cours sorte, mode auto ; boucle bornée à la place
 *     de la récursion (elle biaisait les formes).
 *   - « Factoriser une somme », QCM : « …, factorisé le plus possible,
 *     c'est… » ; facteur commun composé (4, 6, 8, 9, 10) pour que la
 *     factorisation partielle existe TOUJOURS (elle n'était vraiment proposée
 *     que dans 3,2 % des tirages) ; familles constante_non_divisee, partielle
 *     (toujours affichée), signe_perdu quand un moins est en jeu,
 *     facteur_oublie sinon ; le signe moins s'ouvre ; bouche-trou
 *     « g(ux + v + 1) » et échange u(gx + v) retirés. RÉSIDU ACCEPTÉ, comme sur
 *     gen_1230 : la clé partage une composante avec chacun des trois
 *     distracteurs, l'heuristique « le choix qui ressemble le plus aux autres »
 *     la désigne encore dans la quasi-totalité des tirages.
 *   - « Vrai ou faux : un développement » : les égalités fausses ne recopient
 *     plus aucun nombre du membre de gauche (termes_non_semblables
 *     « 3(5x + 1) = 18x », signe_perdu « 4(2x − 3) = 8x + 12 », carre_oublie
 *     « 3x(4 + 8x) = 12x + 24x ») et des vraies en recopient (coefficient 1 :
 *     « 5(x − 3) = 5x − 15 ») ; la forme px(a ± bx) entre ; une à trois vraies
 *     sur quatre. « Un nombre recopié, donc faux » résolvait 94,4 % des
 *     tableaux. Consigne sans méthode.
 *   - Mode auto, À ARBITRER : 5(x + 4) = 5x + 20 ne sort jamais, sous aucune de
 *     ses formes (sacrifié de la fiche de gen_1230, même geste en 3e,
 *     « sacrifiés partagés » du cadrage 158-163).
 *   - Aucun solveur test-side ne couvre ce gen (le solveur
 *     calcul-litteral-4e.js ne couvre que 1106 à 1111) : la preuve est le
 *     second chemin ci-dessous et le test de mesure de la revue.
 *
 * ── Révision 2026-09-24 (demandes du prof, validées) ──────────────────────
 *   - Saisies LIBRES sur les trois cartes de calcul (l'élève tape *, le
 *     champ affiche × ; la lettre x reste la variable) :
 *       · « Développer un produit » : juste développer, SANS LISTE : le
 *         correcteur calcule (comparaison 'developpement',
 *         utils/developpement.js). Toute écriture égale au produit et sans
 *         parenthèse autour d'une somme est juste, termes calculés (72x − 27)
 *         ou laissés en produit (9×8x − 9×3, 9×(−3), 9×7x², 63x×x), dans tout
 *         ordre ; jamais le produit de départ. La liste finie d'abord codée
 *         refusait 9x×2 + 9×7x² (vu par le prof le 24/09 ; mesure : toute
 *         écriture « 9×7x² », « 9×7×x² » ou « 36x×x » refusée). La saisie à
 *         trous est RETIRÉE (clé developper_texte_trou_saisie supprimée).
 *     Les deux autres gardent la liste finie des écritures justes
 *     (comparaison 'expression'), chacune relue par valeurEcriture (second
 *     chemin, throw sinon) :
 *       · « Développer puis réduire » : la forme réduite seulement, tous ses
 *         ordres.
 *       · « Factoriser une somme » : « factorise le plus possible » ; la
 *         factorisation la plus poussée seulement, facteur devant ou derrière
 *         (6(2x − 5), (2x − 5) × 6), parenthèse dans ses deux ordres, facteur
 *         opposé admis ; une factorisation partielle (3(4x − 10)) reste
 *         refusée.
 *     « expression = … » admis devant. Lignes grises de clavier (« Pour
 *     multiplier, tape *. », « Pour le carré, tape ² ou ^2. »), retirées à
 *     l'impression.
 *   - Saisies GUIDÉES (clés reduire_ et factoriser_texte_trou_saisie gardées),
 *     « comme un cours » : l'expression puis les lignes annotées du cours
 *     (« on développe », « on calcule », « on réduit » ; « on fait apparaître
 *     6 », « on factorise par 6 »). Chaque ligne, trous remplis, est vérifiée
 *     égale à l'expression ; chaque trou est collé à x ou à x², ou dans une
 *     différence : aucun échange de trous ne donne une autre réponse juste.
 *
 * INVARIANTS : chaque égalité affichée ou attendue est vérifiée en valeur en
 * cinq points (un polynôme de degré 2 au plus est fixé par trois), chaque
 * distracteur de QCM et chaque ligne fausse est vérifiée DIFFÉRENTE de la clé
 * de la même façon ; le facteur de la clé de factorisation est le pgcd des deux
 * coefficients (throw sinon) ; boucles bornées, throw si elles s'épuisent.
 */

import {
    randomChoice, randInt, shuffle, tex, ecrituresPolynome,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 159;

const L_DEV = 'Développer un produit';
const L_RED = 'Développer puis réduire';
const L_FAC = 'Factoriser une somme';
const L_EGAL = 'Vrai ou faux : un développement';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Rappel brouillon (décision du 2026-09-23, par attendu), IMPÉRATIF sur toutes
// les cartes : l'attendu se fait à la main. Placé juste avant la question, il
// ne souffle aucune méthode.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';

// ── Échelons de difficulté (FORMATS §0.8) ────────────────────────────────
// Part des tirages à coefficient décimal sur les saisies (libre et guidée) de
// « Développer puis réduire » : un sur quatre (0 = jamais, 1 = toujours). Mode
// auto, À ARBITRER.
const PART_COEFFICIENT_DECIMAL = 1 / 4;
// Valeur du marqueur contenu.difficulte posé sur ces tirages, et eux seuls.
const DIFFICULTE_DECIMAL = 'coefficient_decimal';
// d décimal, k pair : k × d est toujours entier (6 × 1,5 = 9).
const DECIMAUX = [0.5, 1.5, 2.5];
const K_PAIRS = [2, 4, 6, 8];

/**
 * Les échelons de la carte « Développer puis réduire », DU PLUS SIMPLE AU PLUS
 * COÛTEUX (l'ordre du tableau est celui de la progression). Donnée pure, jamais
 * lue à l'exécution, sur le modèle de gen_142 ; `pose: false` = l'échelon sort
 * déjà des tirages mais rien ne le marque.
 */
export const ECHELONS_DIFFICULTE = [
    {
        code: 'coefficients_entiers',
        pose: false,
        geste: 'développer puis réduire avec des coefficients entiers : px + qx(r ± sx), k(a − bx) − cx',
    },
    {
        code: DIFFICULTE_DECIMAL,
        pose: true,
        geste: 'k(a − d·x) − c·x avec d décimal (0,5 ; 1,5 ; 2,5) et k pair : il faut calculer k × d, entier (6 × 1,5 = 9), exemple de réussite 6(3 − 1,5x) − 9x',
    },
];

// ── Outils ───────────────────────────────────────────────────────────────

const pgcd = (u, v) => (v === 0 ? u : pgcd(v, u % v));
/** Monôme en x : jamais « 1x ». */
const mono = (c) => (c === 1 ? 'x' : `${c}x`);
/** Monôme en x² : jamais « 1x^2 ». */
const monoCarre = (c) => (c === 1 ? 'x^2' : `${c}x^2`);
/** Décimal en LaTeX : 1,5 → « 1{,}5 » (sans accolades, KaTeX espace la virgule). */
const dec = (d) => String(d).replace('.', '{,}');
/** Valeur ± selon le signe affiché. */
const sg = (signe) => (signe === '+' ? 1 : -1);

// Second chemin : deux expressions (fonctions de x) égales en cinq points,
// donc égales comme polynômes de degré 2 au plus.
const POINTS = [-2, -1, 0, 1, 3];
const egales = (f, g) => POINTS.every((x) => Math.abs(f(x) - g(x)) < 1e-9);
const verifierEgalite = (f, g, quoi) => { if (!egales(f, g)) throw new Error(`gen_159 : égalité fausse (${quoi})`); };
const verifierDifferent = (f, g, quoi) => { if (egales(f, g)) throw new Error(`gen_159 : distracteur égal à la clé (${quoi})`); };

/** Sacrifié de la fiche de gen_1230 : 5(x + 4) = 5x + 20, sous toutes ses formes. */
const estSacrifie = (k, a, b, signe) => k === 5 && a === 1 && b === 4 && signe === '+';

// ── Saisies libres et lignes guidées (révision 2026-09-24) ───────────────

// Lignes grises de clavier : retirées à l'impression comme le rappel brouillon.
// À l'écran, l'étoile tapée s'affiche × (comparaison d'expression).
const AIDE_FOIS = '<p class="italic text-slate-400">Pour multiplier, tape *.</p>';
const AIDE_CARRE = '<p class="italic text-slate-400">Pour le carré, tape ² ou ^2.</p>';

/** Une écriture ramenée à la forme du juge : sans espace, *, ^2, point décimal. */
const normaliser = (s) => String(s).toLowerCase()
    .replace(/\\times/g, '*').replace(/\{,\}/g, '.').replace(/\\mathrm\{\}/g, '')
    .replace(/[\s  ]+/g, '').replace(/[−–]/g, '-').replace(/[×·⋅]/g, '*')
    .replace(/²/g, '^2').replace(/,/g, '.');

/** Monôme SANS son signe, en écriture normalisée : 72x, 72*x, x, 4x^2, 27. */
const monoN = (c, d, etoile = false) => {
    const a = Math.abs(c);
    if (d === 0) return String(a);
    const lettre = d === 1 ? 'x' : 'x^2';
    return a === 1 ? lettre : `${a}${etoile ? '*' : ''}${lettre}`;
};

/** Monômes signés {c, d} en LaTeX, dans l'ordre : 72x, −27 → « 72x - 27 ». */
const latexTermes = (termes) => termes.map((t, i) => {
    const corps = t.d === 0 ? String(Math.abs(t.c)) : t.d === 1 ? mono(Math.abs(t.c)) : monoCarre(Math.abs(t.c));
    if (i === 0) return t.c < 0 ? `-${corps}` : corps;
    return `${t.c < 0 ? '-' : '+'} ${corps}`;
}).join(' ');

const valeurTerme = (t, x) => t.c * x ** t.d;

/**
 * Valeur en x d'une écriture normalisée (chiffres, x, + - * ^ ( ) et produits
 * implicites « 8x », « 6(…) », « (…)*6 ») : le second chemin qui prouve que
 * chaque écriture admise et chaque ligne guidée valent l'expression posée.
 */
const valeurEcriture = (s, x) => {
    let i = 0;
    const lireNombre = () => {
        const m = s.slice(i).match(/^\d+(?:\.\d+)?/);
        if (!m) throw new Error(`gen_159 : nombre attendu dans ${s}`);
        i += m[0].length;
        return Number(m[0]);
    };
    let somme = null;
    const facteur = () => {
        if (s[i] === '(') {
            i += 1;
            const v = somme();
            if (s[i] !== ')') throw new Error(`gen_159 : parenthèse dans ${s}`);
            i += 1;
            return v;
        }
        if (s[i] === 'x') { i += 1; return x; }
        if (s[i] === '-') { i += 1; return -facteur(); }
        return lireNombre();
    };
    const puissance = () => {
        const v = facteur();
        if (s[i] !== '^') return v;
        i += 1;
        return v ** lireNombre();
    };
    const produit = () => {
        let v = puissance();
        while (i < s.length && (s[i] === '*' || s[i] === '(' || s[i] === 'x' || /\d/.test(s[i]))) {
            if (s[i] === '*') i += 1;
            v *= puissance();
        }
        return v;
    };
    somme = () => {
        let v;
        if (s[i] === '-') { i += 1; v = -produit(); } else { if (s[i] === '+') i += 1; v = produit(); }
        while (s[i] === '+' || s[i] === '-') {
            const op = s[i];
            i += 1;
            const t = produit();
            v = op === '+' ? v + t : v - t;
        }
        return v;
    };
    const v = somme();
    if (i !== s.length) throw new Error(`gen_159 : écriture illisible ${s}`);
    return v;
};

/** Chaque écriture admise (et le membre de gauche d'un « = ») vaut f en cinq points. */
const verifierEcritures = (ecritures, f, quoi) => ecritures.forEach((e) => e.split('=').forEach((cote) => {
    if (!POINTS.every((x) => Math.abs(valeurEcriture(cote, x) - f(x)) < 1e-9)) {
        throw new Error(`gen_159 : écriture admise fausse (${quoi}) : ${e}`);
    }
}));

/** Les lignes guidées, trous remplis, valent toutes l'expression posée. */
const verifierLignes = (phrase, f, quoi) => phrase.split('\n').forEach((ligne) => {
    const ecrit = normaliser(ligne.replace(/\{\{([^{}]+)\}\}/g, '$1').replace(/\$/g, '').replace(/\\quad.*$/, ''))
        .replace(/^=/, '');
    if (!POINTS.every((x) => Math.abs(valeurEcriture(ecrit, x) - f(x)) < 1e-9)) {
        throw new Error(`gen_159 : ligne guidée fausse (${quoi}) : ${ligne}`);
    }
});

/** Une ligne guidée en KaTeX, avec sa note de cours à droite (« ← on réduit »). */
const ligneAnnotee = (ligne, note, noteMath = '') => `$${ligne} \\quad \\leftarrow \\text{ ${note} }${noteMath}$`;

// ═════════ Carte 1 : Développer ═════════

/**
 * Tirage de « Développer un produit » (formes du plafond) : k(ax ± b),
 * k(b ± ax) (constante en tête, cours : 2(3 + x) = 6 + 2x) et px(a ± bx). Rend
 * le facteur F et les deux termes de la parenthèse, signés, en {c, d}.
 */
const tirerDev = () => {
    for (let essai = 0; essai < 50; essai++) {
        const forme = randomChoice(['kParenth', 'kParenth', 'constanteEnTete', 'lettreParenth', 'lettreParenth']);
        const signe = randomChoice(['+', '-']);
        const s = sg(signe);
        if (forme === 'lettreParenth') {
            const p = randInt(2, 9); const a = randInt(1, 9); const b = randInt(1, 9);
            return {
                expr: `${p}x(${a} ${signe} ${mono(b)})`, F: { c: p, d: 1 },
                termes: [{ c: a, d: 0 }, { c: s * b, d: 1 }], params: { forme, p, a, b, signe },
            };
        }
        const k = randInt(2, 9); const a = randInt(1, 9); const b = randInt(1, 9);
        if (estSacrifie(k, a, b, signe)) continue;
        return forme === 'kParenth'
            ? { expr: `${k}(${mono(a)} ${signe} ${b})`, F: { c: k, d: 0 }, termes: [{ c: a, d: 1 }, { c: s * b, d: 0 }], params: { forme, k, a, b, signe } }
            : { expr: `${k}(${b} ${signe} ${mono(a)})`, F: { c: k, d: 0 }, termes: [{ c: b, d: 0 }, { c: s * a, d: 1 }], params: { forme, k, a, b, signe } };
    }
    throw new Error('gen_159 : développer sans tirage');
};

// Saisie libre (2026-09-24, décision prof) : « Développe 9(8x − 3). » ; juste
// développer. Pas de liste : le correcteur calcule (comparaison
// 'developpement', utils/developpement.js) ; toute écriture égale au produit
// et sans parenthèse autour d'une somme est juste, « 9(8x − 3) = … » devant
// aussi. Remplace la saisie à trous.
const genDevSaisie = () => {
    const { expr, F, termes, params } = tirerDev();
    const f = (x) => valeurTerme(F, x) * termes.reduce((acc, t) => acc + valeurTerme(t, x), 0);
    const developpes = termes.map((t) => ({ c: F.c * t.c, d: F.d + t.d }));
    const bonne = latexTermes(developpes);
    // second chemin : le résultat affiché, relu par valeurEcriture, vaut le produit de départ
    verifierEcritures([normaliser(bonne)], f, expr);
    const aCarre = developpes.some((t) => t.d === 2);
    const payload = makeSaisie(tex(bonne),
        `${RAPPEL_BROUILLON}<p><b>Développe</b> ${tex(expr)}.</p>${AIDE_FOIS}${aCarre ? AIDE_CARRE : ''}`,
        { ...opts(L_DEV), strict: true, comparaison: 'developpement' });
    payload._meta.params = params;
    return payload;
};

const genDevQcm = () => {
    for (let essai = 0; essai < 50; essai++) {
        const k = randInt(2, 9);
        const signe = randomChoice(['+', '-']);
        const s = sg(signe);
        // Par construction : avec « − », a > b, donc termes_non_semblables
        // (ka − kb)x garde un coefficient positif (au moins 2) ; avec « + »,
        // k = b = 2 est écarté, addition_pour_produit (k + b) y vaudrait kb.
        let a; let b;
        if (signe === '-') { b = randInt(1, 8); a = randInt(b + 1, 9); } else { a = randInt(1, 9); b = randInt(1, 9); }
        if (signe === '+' && k === 2 && b === 2) continue;
        if (estSacrifie(k, a, b, signe)) continue;
        const expr = `${k}(${mono(a)} ${signe} ${b})`;
        const f = (x) => k * (a * x + s * b);
        const cle = { texte: `${k * a}x ${signe} ${k * b}`, v: (x) => (k * a) * x + s * k * b };
        const faux = [
            { code: 'un_seul_terme', texte: `${k * a}x ${signe} ${b}`, v: (x) => (k * a) * x + s * b },
            { code: 'termes_non_semblables', texte: mono(k * a + s * k * b), v: (x) => (k * a + s * k * b) * x },
            signe === '-'
                ? { code: 'signe_perdu', texte: `${k * a}x + ${k * b}`, v: (x) => (k * a) * x + k * b }
                : { code: 'addition_pour_produit', texte: `${k * a}x + ${k + b}`, v: (x) => (k * a) * x + (k + b) },
        ];
        verifierEgalite(f, cle.v, expr);
        faux.forEach((d) => verifierDifferent(f, d.v, `${expr} / ${d.code}`));
        const choix = [cle, ...faux].map((c) => tex(c.texte));
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, choix[0],
            `${RAPPEL_BROUILLON}<p><b>${tex(expr)}, développé, c'est…</b></p>`, opts(L_DEV));
        payload.contenu = { ...payload.contenu, sousType: 'developper' };
        payload._meta.params = { k, a, b, signe, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_159 : développer (QCM) sans tirage');
};

// ═════════ Carte 2 : Développer puis réduire ═════════

/**
 * Tirage de « Développer puis réduire » : px + qx(r ± sx), k(a − bx) − cx et,
 * un tirage sur quatre, k(a − d·x) − c·x à coefficient décimal (k pair, k × d
 * entier). Rend la forme réduite (monômes signés) et les trois lignes guidées,
 * du développement à la réduction.
 */
const tirerRed = () => {
    if (Math.random() < PART_COEFFICIENT_DECIMAL) {
        // k(a − d·x) − c·x = ka − (kd + c)x, k pair donc kd entier
        const k = randomChoice(K_PAIRS); const a = randInt(1, 9); const d = randomChoice(DECIMAUX); const c = randInt(1, 9);
        const kd = k * d;
        if (!Number.isInteger(kd)) throw new Error('gen_159 : k × d non entier');
        return {
            expr: `${k}(${a} - ${dec(d)}x) - ${mono(c)}`,
            f: (x) => k * (a - d * x) - c * x,
            reduit: [{ c: k * a, d: 0 }, { c: -(kd + c), d: 1 }],
            lignes: [
                `= ${k} \\times ${a} - ${k} \\times ${dec(d)}x - ${mono(c)}`,
                `= {{${k * a}}} - {{${kd}}}x - ${mono(c)}`,
                `= {{${k * a}}} - {{${kd + c}}}x`,
            ],
            difficulte: DIFFICULTE_DECIMAL,
            params: { forme: 'kmoins_decimal', k, a, d, c },
        };
    }
    if (randomChoice(['mul', 'kmoins']) === 'mul') {
        // px + qx(r ± sx) = (p + qr)x ± qs·x²
        const p = randInt(2, 20); const q = randInt(2, 9); const r = randInt(2, 9); const s = randInt(1, 9);
        const signe = randomChoice(['+', '-']);
        const e = sg(signe);
        return {
            expr: `${p}x + ${q}x(${r} ${signe} ${mono(s)})`,
            f: (x) => p * x + q * x * (r + e * s * x),
            reduit: [{ c: p + q * r, d: 1 }, { c: e * q * s, d: 2 }],
            lignes: [
                `= ${p}x + ${q}x \\times ${r} ${signe} ${q}x \\times ${mono(s)}`,
                `= ${p}x + {{${q * r}}}x ${signe} {{${q * s}}}x^2`,
                `= {{${p + q * r}}}x ${signe} {{${q * s}}}x^2`,
            ],
            params: { forme: 'mul', p, q, r, s, signe },
        };
    }
    // k(a − bx) − cx = ka − (kb + c)x
    const k = randInt(2, 9); const a = randInt(2, 9); const b = randInt(2, 9); const c = randInt(2, 9);
    return {
        expr: `${k}(${a} - ${b}x) - ${c}x`,
        f: (x) => k * (a - b * x) - c * x,
        reduit: [{ c: k * a, d: 0 }, { c: -(k * b + c), d: 1 }],
        lignes: [
            `= ${k} \\times ${a} - ${k} \\times ${b}x - ${c}x`,
            `= {{${k * a}}} - {{${k * b}}}x - ${c}x`,
            `= {{${k * a}}} - {{${k * b + c}}}x`,
        ],
        params: { forme: 'kmoins', k, a, b, c },
    };
};

// Saisie libre (2026-09-24, décision prof) : « Développe puis réduis … » ; la
// forme RÉDUITE seulement, dans tous ses ordres (ecrituresPolynome : ordre des
// termes, « * » avant x, « + » de tête), « expression = … » devant aussi.
const genRedSaisie = () => {
    const t = tirerRed();
    const ecritures = ecrituresPolynome(t.reduit.map(({ c, d }) => ({ coef: c, deg: d }))).map(normaliser);
    const admises = [...ecritures, ...ecritures.map((e) => `${normaliser(t.expr)}=${e}`)];
    verifierEcritures(admises, t.f, t.expr);
    const aCarre = t.reduit.some((m) => m.d === 2);
    const payload = makeSaisie(tex(latexTermes(t.reduit)),
        `${RAPPEL_BROUILLON}<p><b>Développe puis réduis</b> ${tex(t.expr)}.</p>${aCarre ? AIDE_CARRE : ''}`,
        { ...opts(L_RED), strict: true, comparaison: 'expression', reponses_acceptees: admises });
    if (t.difficulte) payload.contenu = { ...payload.contenu, difficulte: t.difficulte };
    payload._meta.params = { ...t.params, ecritures: admises.length, ...(t.difficulte ? { difficulte: t.difficulte } : {}) };
    return payload;
};

// Saisie guidée (2026-09-24, décision prof : « comme un cours presque ») :
// l'expression, puis trois lignes annotées comme le cours (on développe, on
// calcule, on réduit). Chaque trou est collé à x ou à x², ou seul face à un
// terme en x : échanger deux trous change l'expression, aucune réponse juste
// ne peut être refusée.
const NOTES_REDUIRE = ['on développe', 'on calcule', 'on réduit'];

const genRedGuide = () => {
    const t = tirerRed();
    const phrase = [`$${t.expr}$`, ...t.lignes.map((l, i) => ligneAnnotee(l, NOTES_REDUIRE[i]))].join('\n');
    verifierLignes(phrase, t.f, t.expr);
    const payload = makeTexteTrou(phrase,
        `${RAPPEL_BROUILLON}<p><b>Complète pour développer puis réduire</b> ${tex(t.expr)}.</p>`,
        { ...opts(L_RED), mode: 'saisie' });
    if (t.difficulte) payload.contenu = { ...payload.contenu, difficulte: t.difficulte };
    payload._meta.params = { ...t.params, ...(t.difficulte ? { difficulte: t.difficulte } : {}) };
    return payload;
};

const genRedQcm = () => {
    for (let essai = 0; essai < 50; essai++) {
        // px + qx(r − sx) = (p + qr)x − qs·x²
        const p = randInt(2, 20); const q = randInt(2, 9); const r = randInt(2, 9); const s = randInt(1, 9);
        const expr = `${p}x + ${q}x(${r} - ${mono(s)})`;
        const f = (x) => p * x + q * x * (r - s * x);
        const c1 = p + q * r;
        const cle = { texte: `${c1}x - ${monoCarre(q * s)}`, v: (x) => c1 * x - q * s * x * x };
        // x × x écrit x : (p + qr − qs)x ; si ce coefficient tombe à 0 ou en
        // dessous, repli nommé un_seul_terme (q ne multiplie que r).
        const cx = c1 - q * s;
        const faux = [
            { code: 'priorite', texte: `${(p + q) * r}x - ${monoCarre((p + q) * s)}`, v: (x) => (p + q) * r * x - (p + q) * s * x * x },
            { code: 'signe_perdu', texte: `${c1}x + ${monoCarre(q * s)}`, v: (x) => c1 * x + q * s * x * x },
            cx >= 1
                ? { code: 'carre_oublie', texte: mono(cx), v: (x) => cx * x }
                : { code: 'un_seul_terme', texte: `${c1}x - ${monoCarre(s)}`, v: (x) => c1 * x - s * x * x },
        ];
        verifierEgalite(f, cle.v, expr);
        faux.forEach((d) => verifierDifferent(f, d.v, `${expr} / ${d.code}`));
        const choix = [cle, ...faux].map((c) => tex(c.texte));
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, choix[0],
            `${RAPPEL_BROUILLON}<p><b>${tex(expr)}, développé et réduit, c'est…</b></p>`, opts(L_RED));
        payload.contenu = { ...payload.contenu, sousType: 'reduire' };
        payload._meta.params = { p, q, r, s, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_159 : réduire (QCM) sans tirage');
};

// ═════════ Carte 3 : Factoriser ═════════

/** Le facteur est-il LE plus grand facteur commun ? (second chemin, throw sinon) */
const verifierPlusGrand = (A, B, g) => { if (pgcd(A, B) !== g) throw new Error(`gen_159 : ${g} n'est pas le pgcd de ${A} et ${B}`); };

/**
 * Tirage de « Factoriser une somme » : Ax ± B = g(ux ± v) (constante en tête
 * une fois sur trois, 24 − 8x = 8(3 − x)), Ax² + Bx = gx(ux + w) et
 * Ax² + B = g(ux² + v). Le facteur est TOUJOURS le plus grand (pgcd des deux
 * coefficients, avec x pour Ax² + Bx), vérifié.
 */
const tirerFac = () => {
    for (let essai = 0; essai < 60; essai++) {
        const forme = randomChoice(['affine', 'quadX', 'quadConst']);
        if (forme === 'affine') {
            const g = randInt(2, 9); const u = randInt(1, 9); const v = randInt(1, 9);
            if (pgcd(u, v) !== 1) continue;
            const signe = randomChoice(['+', '-']);
            if (estSacrifie(g, u, v, signe)) continue;
            const s = sg(signe);
            const enTete = Math.random() < 1 / 3;
            verifierPlusGrand(g * u, g * v, g);
            return {
                forme, g, u, v, signe, enTete, facteur: { c: g, d: 0 },
                expr: enTete ? `${g * v} ${signe} ${mono(g * u)}` : `${mono(g * u)} ${signe} ${g * v}`,
                S: enTete ? [{ c: v, d: 0 }, { c: s * u, d: 1 }] : [{ c: u, d: 1 }, { c: s * v, d: 0 }],
                params: { forme, g, u, v, signe, constanteEnTete: enTete },
            };
        }
        if (forme === 'quadX') {
            const g = randInt(2, 6); const u = randInt(2, 9); const w = randInt(1, 9);
            if (pgcd(u, w) !== 1) continue;
            verifierPlusGrand(g * u, g * w, g);
            return {
                forme, g, u, v: w, signe: '+', facteur: { c: g, d: 1 },
                expr: `${g * u}x^2 + ${g * w}x`, S: [{ c: u, d: 1 }, { c: w, d: 0 }], params: { forme, g, u, w },
            };
        }
        const g = randInt(2, 7); const u = randInt(2, 9); const v = randInt(1, 9);
        if (pgcd(u, v) !== 1) continue;
        verifierPlusGrand(g * u, g * v, g);
        return {
            forme, g, u, v, signe: '+', facteur: { c: g, d: 0 },
            expr: `${g * u}x^2 + ${g * v}`, S: [{ c: u, d: 2 }, { c: v, d: 0 }], params: { forme, g, u, v },
        };
    }
    throw new Error('gen_159 : factoriser sans tirage');
};

/**
 * Les écritures justes de la factorisation LA PLUS POUSSÉE (2026-09-24, prof :
 * « factorise le plus possible », mais « (…)*3 » accepté) : le facteur devant
 * (6(2x-5), 6*(2x-5)) ou derrière ((2x-5)*6), 3x ou 3*x, la parenthèse dans
 * ses deux ordres (ecrituresPolynome), et le facteur opposé (-6(-2x+5)), juste
 * aussi. Jamais une factorisation partielle (3(4x-10)).
 */
const ecrituresFactorisation = (facteur, S) => {
    const out = new Set();
    [1, -1].forEach((sgn) => {
        const c = sgn * facteur.c;
        const corpsF = facteur.d === 0 ? [String(Math.abs(c))] : [monoN(c, 1), monoN(c, 1, true)];
        const interieurs = ecrituresPolynome(S.map((t) => ({ coef: sgn * t.c, deg: t.d }))).map(normaliser);
        corpsF.forEach((f) => interieurs.forEach((dedans) => {
            const moins = c < 0 ? '-' : '';
            out.add(`${moins}${f}(${dedans})`);
            out.add(`${moins}${f}*(${dedans})`);
            out.add(`(${dedans})*${c < 0 ? `(-${f})` : f}`);
        }));
    });
    return [...out];
};

const valeurFac = (t) => (x) => valeurTerme(t.facteur, x) * t.S.reduce((acc, m) => acc + valeurTerme(m, x), 0);
const facteurTex = (t) => (t.facteur.d === 0 ? String(t.facteur.c) : `${t.facteur.c}x`);

// Saisie libre (2026-09-24, décision prof) : « Factorise le plus possible … ».
const genFacSaisie = () => {
    const t = tirerFac();
    const f = valeurFac(t);
    verifierEgalite((x) => valeurEcriture(normaliser(t.expr), x), f, t.expr);
    const ecritures = ecrituresFactorisation(t.facteur, t.S);
    const admises = [...ecritures, ...ecritures.map((e) => `${normaliser(t.expr)}=${e}`)];
    verifierEcritures(admises, f, t.expr);
    const aCarre = t.S.some((m) => m.d === 2);
    const payload = makeSaisie(tex(`${facteurTex(t)}(${latexTermes(t.S)})`),
        `${RAPPEL_BROUILLON}<p><b>Factorise le plus possible</b> ${tex(t.expr)}.</p>${AIDE_FOIS}${aCarre ? AIDE_CARRE : ''}`,
        { ...opts(L_FAC), strict: true, comparaison: 'expression', reponses_acceptees: admises });
    payload._meta.params = { ...t.params, ecritures: admises.length };
    return payload;
};

// Saisie guidée (2026-09-24, décision prof : « comme un cours presque ») : la
// méthode du cours, « On fait apparaître le facteur commun 8 » puis « on
// factorise » (24 − 8x = 8 × 3 − 8x = 8(3 − x) ; 7x + 42 = 7x + 7 × 6 =
// 7(x + 6) ; 3x − 3 = 3x − 3 × 1). Trous : les nombres qui restent, chacun
// collé à x ou à x², ou dans une différence ; aucun échange de trous ne donne
// une autre réponse juste.
const genFacGuide = () => {
    const t = tirerFac();
    const { g, u, v, signe } = t;
    const F = facteurTex(t);
    let lignes;
    if (t.forme === 'affine' && t.enTete) {
        lignes = u === 1
            ? [`= ${g} \\times {{${v}}} ${signe} ${g}x`, `= ${g}({{${v}}} ${signe} x)`]
            : [`= ${g} \\times {{${v}}} ${signe} ${g} \\times {{${u}}}x`, `= ${g}({{${v}}} ${signe} {{${u}}}x)`];
    } else if (t.forme === 'affine') {
        lignes = u === 1
            ? [`= ${g}x ${signe} ${g} \\times {{${v}}}`, `= ${g}(x ${signe} {{${v}}})`]
            : [`= ${g} \\times {{${u}}}x ${signe} ${g} \\times {{${v}}}`, `= ${g}({{${u}}}x ${signe} {{${v}}})`];
    } else if (t.forme === 'quadX') {
        lignes = [`= ${g}x \\times {{${u}}}x + ${g}x \\times {{${v}}}`, `= ${g}x({{${u}}}x + {{${v}}})`];
    } else {
        lignes = [`= ${g} \\times {{${u}}}x^2 + ${g} \\times {{${v}}}`, `= ${g}({{${u}}}x^2 + {{${v}}})`];
    }
    const phrase = [
        `$${t.expr}$`,
        ligneAnnotee(lignes[0], 'on fait apparaître', F),
        ligneAnnotee(lignes[1], 'on factorise par', F),
    ].join('\n');
    verifierLignes(phrase, valeurFac(t), t.expr);
    const payload = makeTexteTrou(phrase,
        `${RAPPEL_BROUILLON}<p><b>Complète pour factoriser le plus possible</b> ${tex(t.expr)}.</p>`,
        { ...opts(L_FAC), mode: 'saisie' });
    payload._meta.params = t.params;
    return payload;
};

// Facteur commun COMPOSÉ : la factorisation partielle existe toujours.
const G_COMPOSES = [4, 6, 8, 9, 10];

const genFacQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const g = randomChoice(G_COMPOSES);
        const u = randInt(1, 9); const v = randInt(1, 9);
        if (pgcd(u, v) !== 1) continue;
        const signe = randomChoice(['+', '-']);
        const s = sg(signe);
        const A = g * u; const B = g * v;
        verifierPlusGrand(A, B, g);
        const d = randomChoice([2, 3, 4, 5].filter((x) => g % x === 0 && x < g));
        const expr = `${mono(A)} ${signe} ${B}`;
        const f = (x) => A * x + s * B;
        const cle = { texte: `${g}(${mono(u)} ${signe} ${v})`, v: (x) => g * (u * x + s * v) };
        const partielle = { code: 'partielle', texte: `${d}(${mono((g / d) * u)} ${signe} ${(g / d) * v})`, v: (x) => d * ((g / d) * u * x + s * (g / d) * v) };
        const faux = [
            { code: 'constante_non_divisee', texte: `${g}(${mono(u)} ${signe} ${B})`, v: (x) => g * (u * x + s * B) },
            partielle,
            signe === '-'
                ? { code: 'signe_perdu', texte: `${g}(${mono(u)} + ${v})`, v: (x) => g * (u * x + v) }
                : { code: 'facteur_oublie', texte: `(${mono(u)} + ${v})`, v: (x) => u * x + v },
        ];
        verifierEgalite(f, cle.v, expr);
        // La partielle est une égalité JUSTE, fausse seulement sous « le plus possible ».
        verifierEgalite(f, partielle.v, `${expr} / partielle`);
        faux.filter((x) => x !== partielle).forEach((x) => verifierDifferent(f, x.v, `${expr} / ${x.code}`));
        const choix = [cle, ...faux].map((c) => tex(c.texte));
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, choix[0],
            `${RAPPEL_BROUILLON}<p><b>${tex(expr)}, factorisé le plus possible, c'est…</b></p>`, opts(L_FAC));
        payload.contenu = { ...payload.contenu, sousType: 'factoriser' };
        payload._meta.params = { g, u, v, signe, d, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_159 : factoriser (QCM) sans tirage');
};

// ═════════ Carte 4 : Vrai/faux d'un développement ═════════

/** Les nombres écrits dans une écriture LaTeX, exposant du carré exclu. */
const nombresEcrits = (s) => s.replace(/\^2/g, '').match(/\d+/g) || [];

/**
 * Une ligne « gauche = droite », vraie ou fausse d'une famille attestée. Une
 * fausse ne recopie AUCUN nombre du membre de gauche ; une vraie en recopie
 * quand le coefficient est 1 (« 5(x − 3) = 5x − 15 »).
 */
const tirerLigneVf = (vraie, gauchesVues) => {
    for (let essai = 0; essai < 60; essai++) {
        const forme = randomChoice(['kPlus', 'kMoins', 'pPlus', 'pMoins']);
        const signe = forme.endsWith('Moins') ? '-' : '+';
        const s = sg(signe);
        let gauche; let f; let droite; let v; let code = null;
        if (forme.startsWith('k')) {
            const k = randInt(2, 9); const b = randInt(1, 9);
            const a = vraie && Math.random() < 0.5 ? 1 : randInt(2, 9);
            if (estSacrifie(k, a, b, signe)) continue;
            gauche = `${k}(${mono(a)} ${signe} ${b})`;
            f = (x) => k * (a * x + s * b);
            if (vraie) { droite = `${k * a}x ${signe} ${k * b}`; v = (x) => (k * a) * x + s * k * b; }
            else if (forme === 'kPlus') { code = 'termes_non_semblables'; droite = `${k * a + k * b}x`; v = (x) => (k * a + k * b) * x; }
            else { code = 'signe_perdu'; droite = `${k * a}x + ${k * b}`; v = (x) => (k * a) * x + k * b; }
        } else {
            const p = randInt(2, 9);
            const a = vraie ? randInt(1, 9) : randInt(2, 9);
            const b = vraie ? randInt(1, 9) : randInt(2, 9);
            gauche = `${p}x(${a} ${signe} ${mono(b)})`;
            f = (x) => p * x * (a + s * b * x);
            if (vraie) { droite = `${p * a}x ${signe} ${monoCarre(p * b)}`; v = (x) => (p * a) * x + s * (p * b) * x * x; }
            else if (forme === 'pPlus' || Math.random() < 0.5) {
                code = 'carre_oublie'; droite = `${p * a}x ${signe} ${p * b}x`; v = (x) => (p * a) * x + s * (p * b) * x;
            } else { code = 'signe_perdu'; droite = `${p * a}x + ${monoCarre(p * b)}`; v = (x) => (p * a) * x + (p * b) * x * x; }
        }
        if (gauchesVues.includes(gauche)) continue;
        if (vraie) verifierEgalite(f, v, gauche);
        else {
            verifierDifferent(f, v, `${gauche} / ${code}`);
            const aGauche = new Set(nombresEcrits(gauche));
            if (nombresEcrits(droite).some((n) => aGauche.has(n))) continue;
        }
        return { gauche, ligne: tex(`${gauche} = ${droite}`), vraie, code, forme };
    }
    return null;
};

const genEgaliteVf = () => {
    for (let essai = 0; essai < 50; essai++) {
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = [];
        for (const vraie of verites) {
            const l = tirerLigneVf(vraie, lignes.map((x) => x.gauche));
            if (!l) break;
            lignes.push(l);
        }
        if (lignes.length !== 4 || new Set(lignes.map((l) => l.ligne)).size !== 4) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), lignes.map((l) => (l.vraie ? 0 : 1)),
            `${RAPPEL_BROUILLON}<p><b>Vrai ou faux ?</b></p>`, opts(L_EGAL));
        payload._meta.params = {
            lignes: lignes.map(({ gauche, vraie, forme }) => ({ gauche, vraie, forme })),
            distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })),
        };
        return payload;
    }
    throw new Error('gen_159 : Vrai/Faux sans tirage');
};

// ═════════ Contrat distracteurs (référence de revue, jamais lu à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'distributivité simple, facteur extérieur positif (2 à 9) ; facteur extérieur négatif et soustraction d\'un produit hors plafond (3e)',
        'quatre écritures distinctes, chaque distracteur vérifié différent de la clé en valeur (cinq points), la clé vérifiée égale ; trois distracteurs garantis par construction, re-tirage borné sinon, jamais d\'option inventée',
        'sacrifié : 5(x + 4) = 5x + 20 sous toutes ses formes (fiche de gen_1230)',
    ],
    developper_qcm_unique: {
        support: 'k(ax ± b), k 2 à 9, a et b 1 à 9 ; avec « − », a > b',
        familles: {
            un_seul_terme: 'kax ± b : le facteur ne multiplie que le premier terme ; DEPP 2de 2025 q.5 « 3(5x + 1) », 20,3 %, PRINCIPALE (gen_1230)',
            termes_non_semblables: '(ka ± kb)x : kax et kb fusionnés ; DEPP 2de 2025 q.5 (18x), nom de gen_1227',
            signe_perdu: 'kax + kb pour k(ax − b) : le moins perdu en développant ; seulement quand un moins est en jeu',
            addition_pour_produit: 'kax + (k + b) : additionner au lieu de multiplier ; DEPP 2de 2025 q.5 (15x + 4) ; seulement avec « + », k = b = 2 écarté (k + b = kb)',
        },
        supprimees: {
            coefficient_non_multiplie: 'kx ± kb (a oublié) : aucune erreur attestée, et avec « constante non multipliée » et « addition » la clé était toujours le choix aux plus grands nombres',
        },
        garantie: 'avec « − », a > b donne (ka − kb)x ≥ 2x ; les quatre écritures diffèrent par construction (b ≠ kb, k + b ≠ kb hors k = b = 2 écarté, un seul terme contre deux, signe opposé)',
    },
    reduire_qcm_unique: {
        support: 'px + qx(r − sx), p 2 à 20, q et r 2 à 9, s 1 à 9 ; clé (p + qr)x − qs·x²',
        familles: {
            priorite: '(p + q)x(r − sx) développé : px + qx additionnés avant de distribuer, l\'erreur attendue sur 17x + 4x(5 − x) = 21x(5 − x)',
            signe_perdu: '(p + qr)x + qs·x² : le moins de la parenthèse perdu',
            carre_oublie: '(p + qr − qs)x : x × x écrit x, les deux termes réduits ensemble ; si ce coefficient tombe à 0 ou en dessous, repli nommé un_seul_terme',
            un_seul_terme: '(p + qr)x − s·x² : qx ne multiplie que le premier terme de la parenthèse (repli de carre_oublie)',
        },
        supprimees: {
            r_non_multiplie: '(p + q)x − qs·x² : trois perturbations d\'une seule composante de la clé, qui restait seule en tête à la majorité dans 100 % des tirages ; le commentaire qui l\'annonçait était faux',
        },
        garantie: 'r ≥ 2 donne (p + q)r ≠ p + qr et p ≥ 2 donne (p + q)s ≠ qs : priorite diffère de la clé ; signe_perdu par le signe du carré ; carre_oublie et un_seul_terme (s ≠ qs) par le terme en x²',
    },
    factoriser_qcm_unique: {
        support: 'g(ux ± v), g composé parmi 4, 6, 8, 9, 10, u et v premiers entre eux (1 à 9) : g est le pgcd des deux coefficients (vérifié) ; « factorisé le plus possible, c\'est… »',
        familles: {
            constante_non_divisee: 'g(ux ± gv) : le facteur retiré du premier terme seulement (12x − 30 → 6(2x − 30)), gen_1230',
            partielle: 'd((g/d)ux ± (g/d)v) : une égalité juste mais pas finie, un facteur commun plus petit (2(6x − 15)) ; toujours affichée, gen_1230',
            signe_perdu: 'g(ux + v) pour Ax − B : le moins perdu (6(2x + 5)), gen_1230 ; seulement avec « − »',
            facteur_oublie: '(ux + v) : le facteur commun oublié devant la parenthèse, gen_1230 ; avec « + »',
        },
        supprimees: {
            echange: 'u(gx + v) : non attesté, et égal à la clé quand u = g (13,0 %)',
            bouche_trou: 'g(ux + v + 1) : aucune erreur derrière (12,2 % des tirages)',
            aucun_terme_divise: 'g(Ax + B) : remplacé par constante_non_divisee, plus fréquent',
        },
        garantie: 'g ≥ 4 : v ≠ gv et le facteur d < g, les quatre écritures diffèrent ; RÉSIDU ACCEPTÉ comme sur gen_1230 : la clé partage une composante avec chacun des trois distracteurs, « le choix qui ressemble le plus aux autres » la désigne',
    },
    egalite_tableau_vf: 'quatre égalités k(ax ± b) = … ou px(a ± bx) = …, une à trois vraies ; fausses sans aucun nombre recopié du membre de gauche : termes_non_semblables « 3(5x + 1) = 18x », signe_perdu « 4(2x − 3) = 8x + 12 », carre_oublie « 3x(4 + 8x) = 12x + 24x » ; vraies qui recopient (coefficient 1)',
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    developper_texte: genDevSaisie,
    developper_qcm_unique: genDevQcm,
    reduire_texte: genRedSaisie,
    reduire_texte_trou_saisie: genRedGuide,
    reduire_qcm_unique: genRedQcm,
    factoriser_texte: genFacSaisie,
    factoriser_texte_trou_saisie: genFacGuide,
    factoriser_qcm_unique: genFacQcm,
    egalite_tableau_vf: genEgaliteVf,
};

const FORMAT_LABELS = {
    texte: 'Saisie', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux', texte_trou_saisie: 'Saisie guidée',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-06 (chantier arbres-4e) ; sert aux exercices complexes du groupe Acquis.
export const CATEGORIES = [
    { id: 'developper', label: L_DEV, etapes: 1, formatsList: buildFormats('developper') },
    { id: 'reduire', label: L_RED, etapes: 2, formatsList: buildFormats('reduire') },
    { id: 'factoriser', label: L_FAC, etapes: 2, formatsList: buildFormats('factoriser') },
    { id: 'egalite', label: L_EGAL, etapes: 2, formatsList: buildFormats('egalite') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
